"use client";

import { Database, X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { ProjectDataOverviewIssue } from "@/lib/aicheck/project-data-issue-index";
import type {
  AggregatedProjectData,
  AICheckRunResult,
  FreeformCheckResult,
} from "@/types/aicheck";
import { ProjectDataOverview } from "./ProjectDataOverview";

export type ProjectDataOverviewResultMode = "check" | "freeform";

const PROJECT_DATA_OVERVIEW_WINDOW_NAME = "aicheck-project-data-overview";
const PROJECT_DATA_OVERVIEW_WINDOW_ROOT_ID =
  "project-data-overview-window-root";

export function openProjectDataOverviewPopupWindow(): Window | null {
  if (typeof window === "undefined") return null;

  const width = Math.min(1320, Math.max(980, window.screen.availWidth - 120));
  const height = Math.min(920, Math.max(720, window.screen.availHeight - 120));
  const left = Math.max(0, Math.round((window.screen.availWidth - width) / 2));
  const top = Math.max(0, Math.round((window.screen.availHeight - height) / 2));
  const popupWindow = window.open(
    "",
    PROJECT_DATA_OVERVIEW_WINDOW_NAME,
    [
      "popup=yes",
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`,
      "resizable=yes",
      "scrollbars=yes",
    ].join(","),
  );

  if (!popupWindow) return null;
  ensureProjectDataOverviewPopupWindow(popupWindow);
  popupWindow.focus();
  return popupWindow;
}

function ensureProjectDataOverviewPopupWindow(popupWindow: Window) {
  if (
    popupWindow.document.getElementById(PROJECT_DATA_OVERVIEW_WINDOW_ROOT_ID)
  ) {
    return;
  }
  prepareProjectDataOverviewPopupWindow(popupWindow);
}

function prepareProjectDataOverviewPopupWindow(popupWindow: Window) {
  popupWindow.document.open();
  popupWindow.document.write(`<!doctype html>
<html>
  <head>
    <title>Project Data Overview</title>
  </head>
  <body>
    <div id="${PROJECT_DATA_OVERVIEW_WINDOW_ROOT_ID}"></div>
  </body>
</html>`);
  popupWindow.document.close();

  for (const node of document.querySelectorAll(
    'link[rel="stylesheet"], style',
  )) {
    popupWindow.document.head.appendChild(node.cloneNode(true));
  }

  const baseStyles = popupWindow.document.createElement("style");
  baseStyles.textContent = `
    html, body, #${PROJECT_DATA_OVERVIEW_WINDOW_ROOT_ID} {
      min-height: 100%;
      margin: 0;
      background: #f9fafb;
    }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }
  `;
  popupWindow.document.head.appendChild(baseStyles);
}

export function ProjectDataOverviewDialog({
  open,
  popupWindow = null,
  focusKey = 0,
  focusSource = null,
  projectData,
  checkResult = null,
  freeformResult = null,
  mode = "check",
  sourceLabel = null,
  title = "Project Data Overview",
  onModeChange,
  onClose,
  onCheckReferenceClick,
}: {
  open: boolean;
  popupWindow?: Window | null;
  focusKey?: number;
  focusSource?: string | null;
  projectData: AggregatedProjectData | null;
  checkResult?: AICheckRunResult | null;
  freeformResult?: FreeformCheckResult | null;
  mode?: ProjectDataOverviewResultMode;
  sourceLabel?: string | null;
  title?: string;
  onModeChange?: (mode: ProjectDataOverviewResultMode) => void;
  onClose: () => void;
  onCheckReferenceClick?: (issue: ProjectDataOverviewIssue) => void;
}) {
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, open]);

  useEffect(() => {
    if (focusKey < 0) return;
    if (!open || !popupWindow || popupWindow.closed) {
      setPortalContainer(null);
      return;
    }

    let container = popupWindow.document.getElementById(
      PROJECT_DATA_OVERVIEW_WINDOW_ROOT_ID,
    );
    if (!container) {
      ensureProjectDataOverviewPopupWindow(popupWindow);
      container = popupWindow.document.getElementById(
        PROJECT_DATA_OVERVIEW_WINDOW_ROOT_ID,
      );
    }

    setPortalContainer(container);
    popupWindow.focus();

    const handlePopupBeforeUnload = () => {
      setPortalContainer(null);
      onClose();
    };
    popupWindow.addEventListener("beforeunload", handlePopupBeforeUnload);

    const closePoll = window.setInterval(() => {
      if (popupWindow.closed) {
        window.clearInterval(closePoll);
        setPortalContainer(null);
        onClose();
      }
    }, 500);

    return () => {
      window.clearInterval(closePoll);
      popupWindow.removeEventListener("beforeunload", handlePopupBeforeUnload);
    };
  }, [focusKey, onClose, open, popupWindow]);

  useEffect(() => {
    if (focusKey < 0) return;
    if (!open || !popupWindow || popupWindow.closed) return;
    popupWindow.focus();
  }, [focusKey, open, popupWindow]);

  if (!open) return null;

  const hasCheckResult = Boolean(checkResult);
  const hasFreeformResult = Boolean(freeformResult);
  const effectiveMode =
    mode === "freeform" && hasFreeformResult
      ? "freeform"
      : mode === "check" && hasCheckResult
        ? "check"
        : hasFreeformResult
          ? "freeform"
          : "check";
  const activeCheckResult = effectiveMode === "check" ? checkResult : null;
  const activeFreeformResult =
    effectiveMode === "freeform" ? freeformResult : null;
  const showResultModeTabs = hasCheckResult || hasFreeformResult;
  const handleIssueClick = (issue: ProjectDataOverviewIssue) => {
    onCheckReferenceClick?.(issue);
  };

  const content = (
    <div className="min-h-screen bg-gray-50">
      <div className="sticky top-0 z-20 flex items-start justify-between gap-4 border-b border-gray-200 bg-white px-5 py-4 shadow-sm">
        <ProjectDataOverviewHeader
          title={title}
          sourceLabel={sourceLabel}
          showResultModeTabs={showResultModeTabs}
          mode={effectiveMode}
          hasCheckResult={hasCheckResult}
          hasFreeformResult={hasFreeformResult}
          onModeChange={onModeChange}
        />
        <CloseButton onClose={onClose} />
      </div>
      <div className="p-4">
        <ProjectDataOverview
          projectData={projectData}
          checkResult={activeCheckResult}
          freeformResult={activeFreeformResult}
          focusKey={focusKey}
          focusSource={focusSource}
          onCheckReferenceClick={handleIssueClick}
          sourceLabel={sourceLabel}
        />
      </div>
    </div>
  );

  if (portalContainer) {
    return createPortal(content, portalContainer);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="project-data-overview-title"
      className="fixed inset-0 z-[150] flex items-center justify-center bg-black/40 p-4"
    >
      <button
        type="button"
        aria-label="Close project data overview"
        className="absolute inset-0"
        onClick={onClose}
      />
      <div className="relative z-10 flex max-h-[calc(100vh-2rem)] w-full max-w-7xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl">
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
          <ProjectDataOverviewHeader
            title={title}
            sourceLabel={sourceLabel}
            showResultModeTabs={showResultModeTabs}
            mode={effectiveMode}
            hasCheckResult={hasCheckResult}
            hasFreeformResult={hasFreeformResult}
            onModeChange={onModeChange}
          />
          <CloseButton onClose={onClose} />
        </div>
        <div className="min-h-0 overflow-y-auto bg-gray-50 p-4">
          <ProjectDataOverview
            projectData={projectData}
            checkResult={activeCheckResult}
            freeformResult={activeFreeformResult}
            focusKey={focusKey}
            focusSource={focusSource}
            onCheckReferenceClick={(issue) => {
              handleIssueClick(issue);
              if (onCheckReferenceClick) onClose();
            }}
            sourceLabel={sourceLabel}
          />
        </div>
      </div>
    </div>
  );
}

function ProjectDataOverviewHeader({
  title,
  sourceLabel,
  showResultModeTabs,
  mode,
  hasCheckResult,
  hasFreeformResult,
  onModeChange,
}: {
  title: string;
  sourceLabel: string | null;
  showResultModeTabs: boolean;
  mode: ProjectDataOverviewResultMode;
  hasCheckResult: boolean;
  hasFreeformResult: boolean;
  onModeChange?: (mode: ProjectDataOverviewResultMode) => void;
}) {
  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 text-gray-900">
        <Database size={18} className="text-blue-600" />
        <h2
          id="project-data-overview-title"
          className="text-base font-semibold"
        >
          {title}
        </h2>
      </div>
      {sourceLabel && (
        <div className="mt-1 truncate text-xs text-gray-500">{sourceLabel}</div>
      )}
      {showResultModeTabs && (
        <ResultModeTabs
          mode={mode}
          hasCheckResult={hasCheckResult}
          hasFreeformResult={hasFreeformResult}
          onModeChange={onModeChange}
        />
      )}
    </div>
  );
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
      aria-label="Close project data overview"
    >
      <X size={18} />
    </button>
  );
}

function ResultModeTabs({
  mode,
  hasCheckResult,
  hasFreeformResult,
  onModeChange,
}: {
  mode: ProjectDataOverviewResultMode;
  hasCheckResult: boolean;
  hasFreeformResult: boolean;
  onModeChange?: (mode: ProjectDataOverviewResultMode) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Project data issue result type"
      className="mt-3 inline-flex rounded-md border border-gray-200 bg-gray-50 p-1"
    >
      <button
        type="button"
        role="tab"
        aria-selected={mode === "check"}
        disabled={!hasCheckResult || !onModeChange}
        onClick={() => onModeChange?.("check")}
        className={`h-7 rounded px-2.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
          mode === "check"
            ? "bg-white text-gray-900 shadow-sm"
            : "text-gray-500 hover:text-gray-800"
        }`}
      >
        Normal Check
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === "freeform"}
        disabled={!hasFreeformResult || !onModeChange}
        onClick={() => onModeChange?.("freeform")}
        className={`h-7 rounded px-2.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
          mode === "freeform"
            ? "bg-white text-gray-900 shadow-sm"
            : "text-gray-500 hover:text-gray-800"
        }`}
      >
        Freeform Check
      </button>
    </div>
  );
}
